import {
  Users,
  Settings,
  ShieldCheck,
  Monitor,
  Database,
  Building2,
  HelpCircle,
} from 'lucide-react';

/**
 * FishboneDiagram — a dynamic Ishikawa (cause-and-effect) diagram.
 *
 * Design model (see PRD §6.5 and docs/implementation/20260531-120000-CODEX-FishboneMockup.html):
 *   - The fish skeleton (spine, tail, head, diagonal bones) is fixed decorative *chrome*,
 *     drawn with CSS/SVG and marked aria-hidden. It never changes with data.
 *   - The category chips and cause rows are *content*, mapped from the seeded `fishbone`
 *     object via Object.entries — any number of categories / causes renders.
 *   - Static, full-opacity presentation: no select/dim/expand interaction.
 *   - Cause text and the bone live in separate columns so they never overlap.
 *   - Responsive: at <=1100px (or with the Copilot rail open) the chrome is hidden via CSS
 *     and the same branches reflow into stacked category cards.
 */

const CATEGORY_META = {
  People: { color: '#2563eb', side: 'top', Icon: Users },
  Process: { color: '#0d9488', side: 'top', Icon: Settings },
  Policy: { color: '#d97706', side: 'top', Icon: ShieldCheck },
  Technology: { color: '#7c3aed', side: 'bottom', Icon: Monitor },
  Data: { color: '#0ea5e9', side: 'bottom', Icon: Database },
  Environment: { color: '#16a34a', side: 'bottom', Icon: Building2 },
};
const DEFAULT_META = { color: '#475569', side: 'bottom', Icon: HelpCircle };
const INLINE_CAP = 3;

const metaFor = (category) => CATEGORY_META[category] || DEFAULT_META;

function tint(hex, alpha) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function FishboneBranch({ category, causes, side }) {
  const { color, Icon } = metaFor(category);
  const shown = causes.slice(0, INLINE_CAP);
  const overflow = causes.length - shown.length;
  const y1 = side === 'top' ? 8 : 92;
  const y2 = side === 'top' ? 92 : 8;

  return (
    <div
      className="fishbone-branch"
      data-testid={`fishbone-card-${category.toLowerCase()}`}
      style={{ '--cat-color': color }}
      role="group"
      aria-label={`${category}: ${causes.length} cause${causes.length === 1 ? '' : 's'}`}
    >
      <div className="fishbone-chip" style={{ borderColor: tint(color, 0.5), color }}>
        <span
          className="fishbone-chip-ico"
          style={{ background: tint(color, 0.12) }}
          aria-hidden="true"
        >
          <Icon size={18} strokeWidth={2} />
        </span>
        <span className="fishbone-chip-label">{category}</span>
        <span className="fishbone-chip-count">{causes.length}</span>
      </div>

      <div className="fishbone-body">
        <ul className="fishbone-causes">
          {shown.map((cause) => (
            <li key={cause}>
              <span className="fishbone-cause-text">{cause}</span>
              <svg
                className="fishbone-cause-arrow"
                viewBox="0 0 26 8"
                aria-hidden="true"
                style={{ color: tint(color, 0.85) }}
              >
                <path d="M0 4h22" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <path d="M19 1l4 3-4 3" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </li>
          ))}
          {overflow > 0 && <li className="fishbone-more">+{overflow} more</li>}
        </ul>
        <div className="fishbone-bonezone" aria-hidden="true">
          <svg className="fishbone-bone" viewBox="0 0 100 100" preserveAspectRatio="none">
            <line
              x1="6"
              y1={y1}
              x2="92"
              y2={y2}
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity="0.9"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function FishboneDiagram({ fishbone, effect }) {
  const entries = Object.entries(fishbone || {});
  const topRow = entries.filter(([category]) => metaFor(category).side === 'top');
  const bottomRow = entries.filter(([category]) => metaFor(category).side === 'bottom');
  const headLabel = effect || 'Root cause';

  return (
    <div className="fishbone-figure" data-testid="fishbone-diagram">
      <div className="fishbone-row fishbone-row--top">
        {topRow.map(([category, causes]) => (
          <FishboneBranch key={category} category={category} causes={causes || []} side="top" />
        ))}
      </div>

      <div className="fishbone-spine-row">
        <div className="fishbone-spine" aria-hidden="true" />
        <svg
          className="fishbone-tail"
          width="48"
          height="70"
          viewBox="0 0 62 92"
          aria-hidden="true"
        >
          <path d="M62 46 L6 8 Q0 12 4 22 L20 46 L4 70 Q0 80 6 84 Z" fill="#8893a8" />
          <path d="M62 46 L20 46 L4 70 Q0 80 6 84 Z" fill="#788299" />
        </svg>
        <svg
          className="fishbone-head"
          viewBox="0 0 210 132"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M4 18 Q4 6 18 6 L150 6 Q175 6 200 60 Q206 66 200 72 Q175 126 150 126 L18 126 Q4 126 4 114 Z"
            fill="#1e3a5f"
            stroke="#0f2742"
            strokeWidth="2"
          />
          <circle cx="150" cy="40" r="7" fill="#0f2742" />
          <circle cx="150" cy="40" r="2.4" fill="#cfe0ff" />
        </svg>
        <div className="fishbone-head-label" data-testid="fishbone-head">
          {headLabel}
        </div>
      </div>

      <div className="fishbone-row fishbone-row--bottom">
        {bottomRow.map(([category, causes]) => (
          <FishboneBranch key={category} category={category} causes={causes || []} side="bottom" />
        ))}
      </div>
    </div>
  );
}
